import React, { useEffect } from "react";
import { useQuery } from "react-query";
import Container from "../../lib/Container/Container";
import { ButtonWithIcon, FileDownloader } from "../../lib";
import { ErrorMessage, Field, Form, Formik } from "formik";
import { useNavigate, useSearchParams } from "react-router-dom";

import WomenListTable from "./ReportWomenListTable";
import { getWomenFanCard } from "../../../../api/womenFanCard/womenFanCardApi";
import ReportWomenListTable from "./ReportWomenListTable";
import { getWomenFanExcelReportList } from "../../../../api/report/reportSpecialApi";

const ReportWomenFanCardList = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const pageFromQuery = searchParams.get("page");
  const filterFromQuery = searchParams.get("filter");
  const searchFromQuery = searchParams.get("search");
  const numberInPageFromQuery = searchParams.get("numberInPage");
  
  const page = pageFromQuery ? parseInt(pageFromQuery) : 1;
  const numberInPage = numberInPageFromQuery ? parseInt(numberInPageFromQuery) : 5;

  const [queryString, setQueryString] = React.useState<{
    page: number;
    search: string;
    filter: "pending" | "accepted" | "rejected" | "";
    numberInPage: number;
  }>({
    page,
    search: searchFromQuery || "",
    filter: filterFromQuery as "pending" | "accepted" | "rejected" | "" || "",
    numberInPage: numberInPage,
  });

  const { data, isLoading, isError, error, refetch } = useQuery(
    ["WomenFanCard", queryString],
    () => {
      // فقط مقادیر معتبر فیلتر ارسال شوند
      const validFilters = ["pending", "accepted", "rejected"];
      const filterValue = validFilters.includes(queryString.filter) ? queryString.filter : "";

      return getWomenFanCard(
        queryString.page,
        queryString.search,
        filterValue,
        queryString.numberInPage,
        "desc"
      );
    }
  );

  // تابع برای آپدیت پارامترهای URL و state
  const updateQueryParams = (newParams: Partial<typeof queryString>) => {
    const updatedQuery = { ...queryString, ...newParams };
    
    // ساخت پارامترهای URL
    const params: Record<string, string> = {
      page: String(updatedQuery.page),
      numberInPage: String(updatedQuery.numberInPage),
      orderby: "desc"
    };

    if (updatedQuery.search) {
      params.search = updatedQuery.search;
    }

    // فقط اگر فیلتر معتبر باشد به URL اضافه شود
    if (["pending", "accepted", "rejected"].includes(updatedQuery.filter)) {
      params.filter = updatedQuery.filter;
    }

    setSearchParams(params);
    setQueryString(updatedQuery);
    refetch();
  };

  const setQS = (page: number) => {
    updateQueryParams({ page });
  };

  const setQSandEnable = (values: Partial<typeof queryString>) => {
    updateQueryParams({ ...values, page: values.page || 1 });
  };

  // هندل کردن تغییر فیلتر
  const handleFilterChange = (filter: typeof queryString.filter) => {
    updateQueryParams({ filter, page: 1 });
  };

  // بهبود useEffect برای همگام سازی بهتر بین URL و state
  useEffect(() => {
    const newPage = searchParams.get("page");
    const newFilter = searchParams.get("filter") as typeof queryString.filter;
    const newSearch = searchParams.get("search");
    const newNumberInPage = searchParams.get("numberInPage");
    
    const updates: Partial<typeof queryString> = {};
    
    if (newPage && parseInt(newPage) !== queryString.page) {
      updates.page = parseInt(newPage);
    }
    
    if (newFilter !== queryString.filter) {
      updates.filter = newFilter || "";
    }
    
    if (newSearch !== queryString.search) {
      updates.search = newSearch || "";
    }
    
    if (newNumberInPage && parseInt(newNumberInPage) !== queryString.numberInPage) {
      updates.numberInPage = parseInt(newNumberInPage);
    }
    
    if (Object.keys(updates).length > 0) {
      setQueryString(prev => ({
        ...prev,
        ...updates
      }));
    }
  }, [searchParams, queryString]);

  if (isLoading) return <div>Loading...</div>;
  if (isError) return <div>Error: </div>;

  return (
    <Container className={"bg-white p-0 mt-10"}>
      <div className={"d-flex align-items-center justify-content-between table-header"}>
        <div className={"d-flex align-items-center gap-4"}>
          <SearchForm
            onSubmitCallback={(searchTerm) => {
              setQSandEnable({ search: searchTerm });
            }}
            searchTerm={queryString.search}
          />
        </div>
      </div>

      {data?.fanCards?.length ? (
        <ReportWomenListTable
          queryString={queryString}
          setQueryString={setQS}
          onFilterChange={handleFilterChange}
          data={data}
          refetch={refetch}
          numberInPage={queryString.numberInPage}
        />
      ) : (
        <p className={"p-2 m-2"}>فیلدی با این خصوصیات پیدا نشد.</p>
      )}
    </Container>
  );
};

const SearchForm = ({ onSubmitCallback, searchTerm }) => {
  const initialValues = {
    searchTerm: searchTerm || "", // Set initial value from prop if provided
  };

  const onSubmit = (values, { setSubmitting }) => {
    setSubmitting(false);

    // Call the onSubmitCallback if provided
    if (onSubmitCallback && typeof onSubmitCallback === "function") {
      onSubmitCallback(values.searchTerm);
    }
  };
  const { data: exportToExcel, refetch: exportToExcelRefetch } = useQuery(
    ["export-to-excel"],
    () => getWomenFanExcelReportList(),
    {
      enabled: false,
    }
  );
  return (
    <>
      <Formik initialValues={initialValues} onSubmit={onSubmit}>
        {({ isSubmitting, handleChange, values }) => (
          <Form
            className={"d-flex align-items-center gap-3"}
            placeholder={undefined}
            onPointerEnterCapture={undefined}
            onPointerLeaveCapture={undefined}
          >
            <Field
              type="text"
              name="searchTerm"
              className="border rounded p-2 bg-white w-c-130 text-right"
              placeholder="جستجو"
              onChange={handleChange}
              value={values.searchTerm}
            />
            <ErrorMessage
              name="searchTerm"
              component="div"
              className="text-red-600"
            />
            <ButtonWithIcon
              btnText="جستجو"
              iconName="magnifier"
              type="submit"
              disabled={isSubmitting}
            />
          </Form>
        )}
      </Formik>

      <FileDownloader
        downloadFunc={() => {
          exportToExcelRefetch();
        }}
      />
    </>
  );
};

export default ReportWomenFanCardList;
